import type { Match } from "@/types/match";

export function formatMinute(match: Pick<Match, "status" | "minute" | "minuteExtra">): string {
  if (match.status === "HALFTIME") return "HT";
  if (match.status === "FULLTIME") return "FT";
  if (match.status === "POSTPONED") return "PP'D";
  if (match.status === "LIVE" && match.minute != null) {
    // FotMob and most score apps show stoppage time as "45+2'" rather than
    // silently pinning the clock at 45 or 90 — without this, a match deep
    // into first-half stoppage looks "stuck" compared to those apps.
    const extra = match.minuteExtra;
    return extra ? `${match.minute}+${extra}'` : `${match.minute}'`;
  }
  return "";
}

export function formatKickoff(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function formatKickoffDate(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function isSameDay(isoA: string, dateB: Date): boolean {
  const a = new Date(isoA);
  return (
    a.getFullYear() === dateB.getFullYear() &&
    a.getMonth() === dateB.getMonth() &&
    a.getDate() === dateB.getDate()
  );
}

/** YYYY-MM-DD in the viewer's local calendar day — what API-Football's
 * date-scoped fixtures endpoint expects. Built from local date parts
 * rather than `toISOString().slice(0, 10)`, which shifts by the UTC
 * offset and can silently return the wrong day near midnight. */
export function toApiDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function teamInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase();
}

/**
 * The viewer's IANA timezone (e.g. "America/New_York"), sent through to
 * API-Football on every fixture request. Without this, API-Football's
 * `date` filter matches against UTC, not the viewer's local calendar day —
 * a match at 11pm local time (early morning UTC the next day) can silently
 * vanish from "today" or appear on the wrong day versus apps like FotMob
 * that always localize to the viewer.
 */
export function getViewerTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
